import React, { useEffect, useState } from "react";
import { Box, Skeleton, Flex } from "@mantine/core";
import { saveIdea } from "../../../../Api";
import { useAuth0 } from "@auth0/auth0-react";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import TagMenu from "../../../Tags-old/TagMenu";
import {useKnowledgeData} from "../../../../hooks/useKnowledgeData";
import {useFields} from "../../../../hooks/useFields";
import {useUserPermissions} from "../../../../hooks/useUserPermissions";

export const TagsCell: React.FC<any> = ({ data, col }) => {
  const { addedAttributes } = useFields();
  const { tagCategories, tagsOptions } = useKnowledgeData();
  const safeAddedAttributes = Array.isArray(addedAttributes) ? addedAttributes : [];
  const safeTagCategories = Array.isArray(tagCategories) ? tagCategories : [];
  const safeTagsOptions = Array.isArray(tagsOptions) ? tagsOptions : [];
  
  const cellAddedAttribute = safeAddedAttributes.find((attr: any) => attr.fieldPath === col.accessor);
  const colCategoryId = cellAddedAttribute?.category;
  const [category, setCategory] = useState<any>(null);
  const [isSingleSelect, setIsSingleSelect] = useState<boolean>(false);
  const { canEditIdeasInline } = useUserPermissions();
  const isEditable = canEditIdeasInline;

  useEffect(() => {
    if (!safeTagCategories || !colCategoryId) {
      setCategory(null);
      return;
    }
    const foundCategory = safeTagCategories.find((cat: any) => cat._id === colCategoryId);
    setCategory(foundCategory || null);
    setIsSingleSelect(!foundCategory?.isMultiSelect);
  }, [safeTagCategories, colCategoryId]);

  // Filter tagsOptions by categoryId if colCategoryId is specified
  const filteredTagsOptions = React.useMemo(() => {
    if (colCategoryId) {
      return safeTagsOptions.filter((tag: any) => tag.categoryId === colCategoryId);
    } else {
      return safeTagsOptions.filter((tag: any) => !tag.categoryId);
    }
  }, [safeTagsOptions, colCategoryId]);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [initialTags, setInitialTags] = useState<string[]>([]);

  const auth0 = useAuth0();

  useEffect(() => {
    const value = getNestedFieldValue(data, col.accessor);
    if (isSingleSelect) {
      // For single select, value can be a string, array, or null/undefined
      // Always store as array, but only use first element for display
      let tagValue: string[] = [];
      if (Array.isArray(value)) {
        tagValue = value.length > 0 ? [value[0]] : [];
      } else if (value && typeof value === 'string') {
        tagValue = [value];
      }
      setSelectedTags(tagValue);
      setInitialTags(tagValue);
    } else {
      // For multi-select, value should be an array
      const tagArray = Array.isArray(value) ? value : [];
      setSelectedTags(tagArray);
      setInitialTags(tagArray);
    }
  }, [data, col.accessor, isSingleSelect]);

  const handleTagToggle = async (tagId: string) => {
    let updatedTags: string[];
    let valueToSave: string | string[] | null;

    if (isSingleSelect) {
      updatedTags = selectedTags.includes(tagId) ? [] : [tagId];
      valueToSave = updatedTags.length > 0 ? updatedTags : null;
    } else {
      if (selectedTags.includes(tagId)) {
        updatedTags = selectedTags.filter((id) => id !== tagId);
      } else {
        updatedTags = [...selectedTags, tagId];
      }
      // Save as array
      valueToSave = updatedTags;
    }

    setSelectedTags(updatedTags);

    // Compare with initial value - both should be arrays now
    const initialValue = isSingleSelect 
      ? (initialTags.length > 0 ? initialTags : null)
      : initialTags;
    
    if (JSON.stringify(valueToSave) !== JSON.stringify(initialValue)) {
      try {
        await saveIdea({ id: data._id, [col.accessor]: valueToSave, extraFields: true }, auth0);
        setInitialTags(updatedTags);
      } catch (error) {
        console.error("Error updating idea:", error);
      }
    }
  };

  const handleClearSelection = async () => {
    if (isSingleSelect && selectedTags.length > 0) {
      const updatedTags: string[] = [];
      const valueToSave = null;

      setSelectedTags(updatedTags);

      const initialValue = initialTags.length > 0 ? initialTags : null;
      
      if (JSON.stringify(valueToSave) !== JSON.stringify(initialValue)) {
        try {
          await saveIdea({ id: data._id, [col.accessor]: valueToSave, extraFields: true }, auth0);
          setInitialTags(updatedTags);
        } catch (error) {
          console.error("Error clearing tag:", error);
          setSelectedTags(initialTags);
        }
      }
    }
  };

  return (
    <Box p={0} m={0} h="100%" w="100%" onMouseMove={(e) => e.stopPropagation()}>
      <TagMenu
        selectedTags={selectedTags}
        columnWidth={col?.width}
        onTagToggle={handleTagToggle}
        tagsOptions={filteredTagsOptions}
        colTitle={category?.name || col?.title || "Tag"}
        singleSelect={isSingleSelect}
        onClearSelection={handleClearSelection}
        isReadOnly={!isEditable}
      />
    </Box>
  );
};

// Skeleton component for TagsCell
export const TagsCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  
  // Tags typically have varied widths, create multiple tag skeletons
  const tagWidths = columnWidth 
    ? [Math.min(parseInt(columnWidth) * 0.25, 60), Math.min(parseInt(columnWidth) * 0.35, 80)]
    : [60, 80];
  
  return (
    <Box 
      sx={{ 
        width: columnWidth || "100%", 
        minWidth, 
        maxWidth 
      }}
    >
      <Flex gap={4} wrap="wrap">
        {tagWidths.map((width, index) => (
          <Skeleton 
            key={index}
            height={22} 
            width={width} 
            radius="xs" 
            sx={{ 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #e9ecef' 
            }} 
          />
        ))}
      </Flex>
    </Box>
  );
};
